import { useCallback } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

export const useThreadAiStateWSHandler = () => {
  const unifiedThreadUpdater = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const fusionEnabled = SMSDataSharedUtils.useGetFusionEnabled();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const fusionPhaseThreeEnabled = getFeatureFlagValue('nwx:fusion-phase-three', 'ALL');

  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.THREAD_AI_STATE>>(
    async (event) => {
      const { params } = event;
      const locationId = params.threadAiActivity.locationId ?? '';

      const weaveNumber = params.threadAiActivity.locationPhoneNumber;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!weaveNumber && !checkUserLocationPhoneAccess(weaveNumber)) {
        return;
      }

      if (fusionEnabled) {
        await unifiedThreadUpdater.upsertUnifiedThread({
          matchValues: {
            threadId: params.threadAiActivity.threadId ?? '',
            locationId,
          },
          updater: (thread) => {
            return thread
              ? {
                  ...thread,
                  aiStatus: {
                    ...thread.aiStatus,
                    status: params.threadAiActivity.status,
                    statusDetails: params.threadAiActivity.details,
                    isActive: params.threadAiActivity.isActive,
                    isEscalated: params.threadAiActivity.isEscalated,
                    isSeeded: params.threadAiActivity.isSeeded,
                    isBotDeactivatedByPhoneConfig: params.threadAiActivity.isBotDeactivatedByPhoneConfig,
                  },
                }
              : undefined;
          },
          isOptimistic: false,
        });

        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadId: params.threadAiActivity.threadId ?? '',
            locationId,
            threadItemId: '',
          },
          updater: () => {
            return {
              aiActivity: {
                ...params.threadAiActivity,
              },
            };
          },
          isOptimistic: false,
        });
      }
    },
    [
      fusionEnabled,
      fusionPhaseThreeEnabled,
      unifiedThreadUpdater.upsertUnifiedThread,
      unifiedThreadUpdater.upsertThreadItem,
    ]
  );

  return wsEventHandler;
};

export const useThreadAiStateWSSubscription = () => {
  const handler = useThreadAiStateWSHandler();
  return useWebsocketEventSubscription(NotificationType.THREAD_AI_STATE, handler);
};
